import { useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Ellipsis } from 'lucide-react'
import Menu, { MenuItem, MenuSeparator } from './Menu'
import { kit } from './strings'

/**
 * The section strip that never wraps: one row, as many sections as the row can hold,
 * and the rest behind a "More" menu at its end.
 *
 * The plain strip wraps, and eighteen sections wrapped into a crowd. A rail beside the
 * panel held them all but took its width from the forms. This holds them in one line by
 * measuring: every section is rendered once so its width is known, then on every resize
 * the row keeps the first ones that fit — the More button's own width reserved — and
 * folds the remainder into the menu. The CURRENT section is always in the row: if it
 * falls in the fold, it trades places with the last one that fits, so where you are is
 * never hidden behind a menu.
 *
 * Items keep their two natures. One with `to` is a link wherever it appears and carries
 * a chevron; one without switches the panel. Groups survive into the menu as separators.
 */
export default function PrioritySectionNav({ groups, value, onChange, ariaLabel }) {
  const items = groups.flatMap((group, groupIndex) =>
    group.items.map((item) => ({ ...item, groupIndex })),
  )

  const rowRef = useRef(null)
  const moreRef = useRef(null)
  const widths = useRef(new Map())
  const [visible, setVisible] = useState(items.length)

  const setItemRef = (id) => (node) => {
    if (node) widths.current.set(id, node.getBoundingClientRect().width)
  }

  useLayoutEffect(() => {
    const row = rowRef.current
    if (!row) return undefined

    const fit = () => {
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0
      const more = moreRef.current?.getBoundingClientRect().width ?? 96
      const room = row.getBoundingClientRect().width - more - gap
      let used = 0
      let count = 0
      for (const item of items) {
        const width = widths.current.get(item.id) ?? 0
        const next = used + width + (count > 0 ? gap : 0)
        if (next > room) break
        used = next
        count += 1
      }
      /* Everything fits with the reserve to spare: no menu, so the reserve is spare too. */
      if (count === items.length - 1 && used + gap + (widths.current.get(items[items.length - 1].id) ?? 0) <= room + more) {
        count = items.length
      }
      setVisible(Math.max(1, count))
    }

    fit()
    const observer = new ResizeObserver(fit)
    observer.observe(row)
    document.fonts?.ready.then(fit)
    return () => observer.disconnect()
    // Measured widths live in a ref; the item list is a constant of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  /* The row, with the current section pulled in if it fell in the fold. */
  let row = items.slice(0, visible)
  let folded = items.slice(visible)
  const current = folded.find((item) => item.id === value)
  if (current && row.length > 0) {
    const swapped = row[row.length - 1]
    row = [...row.slice(0, -1), current]
    folded = [swapped, ...folded.filter((item) => item !== current)]
  }

  const renderContent = (item) => {
    const Icon = item.icon
    return (
      <>
        <span className="section-nav__icon" aria-hidden="true">
          {Icon ? <Icon size={16} strokeWidth={1.9} /> : null}
        </span>
        <bdi className="section-nav__label">{item.label}</bdi>
        {item.to && <ChevronLeft className="section-nav__go" size={14} strokeWidth={2} aria-hidden="true" />}
      </>
    )
  }

  return (
    <nav className="section-nav section-nav--priority" aria-label={ariaLabel}>
      <div ref={rowRef} className="section-nav__row">
        {row.map((item) => {
          const selected = item.id === value
          const className = `section-nav__item${selected ? ' is-selected' : ''}`
          return item.to ? (
            <Link key={item.id} ref={setItemRef(item.id)} to={item.to} className={className} data-testid="tab" data-tab-id={item.id}>
              {renderContent(item)}
            </Link>
          ) : (
            <button
              key={item.id}
              ref={setItemRef(item.id)}
              type="button"
              className={className}
              aria-current={selected ? 'true' : undefined}
              onClick={() => onChange(item.id)}
              data-testid="tab"
              data-tab-id={item.id}
            >
              {renderContent(item)}
            </button>
          )
        })}

        {folded.length > 0 && (
          <Menu
            align="start"
            className="section-nav__more"
            trigger={({ open, toggle }) => (
              <button
                ref={moreRef}
                type="button"
                className={`section-nav__item section-nav__item--more${open ? ' is-open' : ''}`}
                aria-haspopup="true"
                aria-expanded={open}
                onClick={toggle}
              >
                <span className="section-nav__icon" aria-hidden="true">
                  <Ellipsis size={16} strokeWidth={2} />
                </span>
                <bdi className="section-nav__label">{kit.more}</bdi>
              </button>
            )}
          >
            {folded.map((item, index) => (
              <div key={item.id}>
                {index > 0 && folded[index - 1].groupIndex !== item.groupIndex && <MenuSeparator />}
                {item.to ? (
                  <MenuItem as={Link} to={item.to} icon={item.icon}>
                    {item.label}
                  </MenuItem>
                ) : (
                  <MenuItem icon={item.icon} onClick={() => onChange(item.id)}>
                    {item.label}
                  </MenuItem>
                )}
              </div>
            ))}
          </Menu>
        )}
      </div>
    </nav>
  )
}
